import { collection, doc, getDocs, getDoc, addDoc, updateDoc, deleteDoc, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import type { AudioGuide, CreateAudioGuideData, UpdateAudioGuideData } from '../types/audio-guides';

export class AudioGuidesService {
  private static instance: AudioGuidesService;

  static getInstance(): AudioGuidesService {
    if (!AudioGuidesService.instance) {
      AudioGuidesService.instance = new AudioGuidesService();
    }
    return AudioGuidesService.instance;
  }

  // ==================== AUDIO GUIDES ====================

  async getAllAudioGuides(): Promise<AudioGuide[]> {
    try {
      const guidesRef = collection(db, 'lessons');
      const guidesQuery = query(guidesRef, orderBy('order', 'asc'));
      const snapshot = await getDocs(guidesQuery);

      const guides: AudioGuide[] = [];
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        guides.push({
          uid: docSnap.id,
          title: data.title || '',
          bannerText: data.bannerText || '',
          subText: data.subText || '',
          audio_path: data.audio_path || '',
          bannerPath: data.bannerPath || '',
          type: data.type || 'audio',
          status: data.status,
          isFree: data.isFree,
          order: data.order,
          aiGuidance: data.aiGuidance || '',
        });
      });

      return guides;
    } catch (error) {
      console.error('Error fetching audio guides:', error);
      throw error;
    }
  }

  async getAudioGuide(id: string): Promise<AudioGuide | null> {
    try {
      const guideRef = doc(db, 'lessons', id);
      const guideSnap = await getDoc(guideRef);

      if (!guideSnap.exists()) {
        return null;
      }

      const data = guideSnap.data();
      return {
        uid: guideSnap.id,
        title: data.title || '',
        bannerText: data.bannerText || '',
        subText: data.subText || '',
        audio_path: data.audio_path || '',
        bannerPath: data.bannerPath || '',
        type: data.type || 'audio',
        status: data.status,
        isFree: data.isFree,
        order: data.order,
        aiGuidance: data.aiGuidance || '',
      };
    } catch (error) {
      console.error('Error fetching audio guide:', error);
      throw error;
    }
  }

  async createAudioGuide(guideData: CreateAudioGuideData): Promise<string> {
    try {
      const guidesRef = collection(db, 'lessons');
      
      // Always use auto-generated ID
      const docRef = await addDoc(guidesRef, {
        ...guideData,
        status: guideData.status || 'draft',
        isFree: guideData.isFree || false,
        order: guideData.order || 0,
      });
      return docRef.id;
    } catch (error) {
      console.error('Error creating audio guide:', error);
      throw error;
    }
  }

  async updateAudioGuide(id: string, guideData: UpdateAudioGuideData): Promise<void> {
    try {
      const guideRef = doc(db, 'lessons', id);
      await updateDoc(guideRef, { ...guideData });
    } catch (error) {
      console.error('Error updating audio guide:', error);
      throw error;
    }
  }

  async deleteAudioGuide(id: string): Promise<void> {
    try {
      const guideRef = doc(db, 'lessons', id);
      await deleteDoc(guideRef);
    } catch (error) {
      console.error('Error deleting audio guide:', error);
      throw error;
    }
  }
}

export const audioGuidesService = AudioGuidesService.getInstance();
